import React, {useState} from 'react';
import {twMerge} from 'tailwind-merge';

import {
  useLinePlotSettings,
  useWorkspaceLayoutSettings,
} from '../WorkspaceDrawer/Settings/hooks';
import {
  SAVE_WORKSPACE_TEMPLATE_DESCRIPTION,
  UPDATE_WORKSPACE_TEMPLATE_DESCRIPTION,
} from './constants';
import {useTemplateSections} from './useTemplateSections';
import {
  WorkspaceTemplateErrorBanner,
  WorkspaceTemplateInfoBanner,
  WorkspaceTemplateSettingsCorruptedBanner,
} from './WorkspaceTemplateBanners';
import {WorkspaceTemplateContentFooter} from './WorkspaceTemplateContentFooter';
import {WorkspaceTemplateContentHeader} from './WorkspaceTemplateContentHeader';
import {useWorkspaceTemplateDrawerContext} from './WorkspaceTemplateDrawerContextProvider';
import {WorkspaceTemplateSettingsDisplay} from './WorkspaceTemplateSettingsDisplay';

export const WorkspaceTemplateContent = () => {
  const {
    isLoadingWorkspaceTemplates,
    workspaceTemplateMode,
    workspaceTemplates,
    workspaceTemplatesError,
  } = useWorkspaceTemplateDrawerContext();
  const currWorkspaceLinePlotSettings = useLinePlotSettings();
  const currWorkspaceLayoutSettings = useWorkspaceLayoutSettings();

  // This is safe to do since we only support 1 template per user within an org
  // and it's enforced in the sql database table as well.
  const workspaceTemplate = workspaceTemplates[0];
  const lastUpdatedAt = workspaceTemplate?.updatedAt;

  // Template exists but settings failed to parse - show warning
  const hasCorruptedSettings =
    workspaceTemplate != null &&
    workspaceTemplate.linePlotSettings === undefined;

  const [error, setError] = useState<string>();

  const templateSections = useTemplateSections({
    workspaceTemplate,
    currWorkspaceLinePlotSettings,
    currWorkspaceLayoutSettings,
  });

  return (
    <div
      className="flex h-full w-full flex-col overflow-hidden"
      data-test="workspace-template-content">
      <WorkspaceTemplateContentHeader />
      <div
        className={twMerge(
          'flex flex-1 flex-col overflow-y-auto',
          'p-16',
          'w-full'
        )}>
        <p
          className={twMerge(
            'text-base leading-[140%] text-wb-text-primary',
            'mb-20'
          )}>
          {workspaceTemplateMode === 'save'
            ? SAVE_WORKSPACE_TEMPLATE_DESCRIPTION
            : UPDATE_WORKSPACE_TEMPLATE_DESCRIPTION}
        </p>
        {(error || workspaceTemplatesError) && (
          <WorkspaceTemplateErrorBanner
            workspaceTemplateMode={workspaceTemplateMode}
          />
        )}
        {hasCorruptedSettings && <WorkspaceTemplateSettingsCorruptedBanner />}
        <WorkspaceTemplateSettingsDisplay
          linePlotSections={templateSections.linePlotTemplateSections}
          workspaceLayoutSections={
            templateSections.workspaceLayoutTemplateSections
          }
          updatedAt={lastUpdatedAt}
          isLoading={isLoadingWorkspaceTemplates}
          displayContext="drawer"
        />
        {!isLoadingWorkspaceTemplates && <WorkspaceTemplateInfoBanner />}
      </div>
      <WorkspaceTemplateContentFooter setError={setError} />
    </div>
  );
};
